import CheverontWhite from 'images/icon/cheveront_white.svg';
import new_tab_white from 'images/icon/new_tab_white.svg';
import close from 'images/icons/orders/close.svg';
import * as moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import { UserBlock } from '../../../components_v2/dropdown/DropdownOwners';
import { removeWrappers } from '../../../components_v2/filter/model/Model';
import { PaginationResult } from '../../../components_v2/pagination/model/Model';
import Pagination from '../../../components_v2/pagination/Pagination';
import { Translate, translateToString } from '../../../styles/global/translate';
import { DEFAULT_STEP } from '../../authorization/model';
import { DateAccessor } from '../../client-companies/data/CompanyColumns';
import { TableContext } from '../../contact/data/ContactContext';
import { BlackContainer, Card, Close, PhotoContainer, PhotoText } from '../../gallery/style';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import NoData from '../../noData/NoData';
import { MandatoryModalState } from '../../products/model';
import { FlexDiv } from '../../products/style';
import { TOOLBARS_HEIGHT } from '../../globals/defaultToolbar/style/Style';
import { FormInstanceGalleryType, getFormInstancesGallery } from '../actions';
import { CardImageDisplayer } from '../FieldDisplayer';
import { FormInstanceViewSubProps } from './FormInstanceView';

const DEFAULT_PAGINATION = { step: DEFAULT_STEP, currentPage: 1, offset: 0 };

const Container = styled.div<{ noPagination?: boolean }>`
	height: calc(100vh - ${p => TOOLBARS_HEIGHT + (p.noPagination ? 10 : 50)}px);
	overflow: auto;
	gap: 20px 10px;
	display: grid;
  	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
`;

export default function FormInstanceGallery(props: FormInstanceViewSubProps) {
	const [instances, setInstances] = React.useState<FormInstanceGalleryType[]>([]);
	const { formId } = props;
	const context = React.useContext(TableContext);
	const { filterResult, setFilterResult } = context;
	const [pagination, setPagination] = React.useState<PaginationResult>(DEFAULT_PAGINATION);
	const [loadingState, setLoadingState] = React.useState(LoadingStateEnum.LOADING);
	const [modalInfo, setModalInfo] = React.useState<MandatoryModalState<number>>({ isOpen: false });


	React.useEffect(() => {
		props.setToolBarState({
			bottomLeftToolbarComponent: <></>,
			bottomRightToolbarComponent: <></>
		});
	}, []);

	const refresh = React.useCallback((pagination: PaginationResult) => {
		if (!props.viewLoaded) return;
		setLoadingState(LoadingStateEnum.LOADING);
		getFormInstancesGallery(formId, {
			limit: pagination.step ?? DEFAULT_STEP,
			offset: pagination.offset ?? 0,
			filters: filterResult?.formatted == undefined ? undefined : removeWrappers(filterResult.formatted),
		}).then(res => {
			setInstances(res);
			setLoadingState(LoadingStateEnum.LOADED);
		}).catch(_ => {
			setLoadingState(LoadingStateEnum.ERROR);
			setFilterResult(undefined);
		});
	}, [formId, props.viewLoaded, filterResult]);

	React.useEffect(() => {
		setPagination(DEFAULT_PAGINATION);
		refresh(DEFAULT_PAGINATION);
	}, [refresh]);
	const closeModal = React.useCallback(() => setModalInfo({ isOpen: false, data: undefined }), []);

	if (instances.length === 0) {
		return <Container noPagination>
			<NoData
				height='100%'
				message={<Translate id="global.nodata.message.form" />}
				messageButton={<Translate id="global.nodata.button.form" />}
				urlButton="https://intercom.help/sidely-university/fr"
			/>
			<ComponentLoader loadingState={loadingState} allScreen />
		</Container>;
	}

	return <>
		{modalInfo.isOpen && <BlackContainer>
			<PhotosList closeModal={closeModal} formId={formId} instances={instances} defaultInstanceIndex={modalInfo.data} />
			<Close src={close} onClick={closeModal} />
		</BlackContainer>}
		<Container>
			{instances.map((instance, instanceIndex) => {
				const images = instance.images.reduce((acc: string[], image) => {
					acc.push(...image.value.map(value => value.url_thumb));
					return acc;
				}, []);
				return <Card key={instance.uuid} onClick={() => setModalInfo({ isOpen: true, data: instanceIndex })}>
					<PhotoContainer>
						<CardImageDisplayer images={images} />
					</PhotoContainer>
					<PhotoText size={13} weight={600}>
						{instance.linked_company_name}
					</PhotoText>
					<PhotoText maxLines={1.5}>
						<UserBlock userId={instance.created_by} />
					</PhotoText>
					<PhotoText size={9}>
						<DateAccessor date={instance.made_at} utc />
					</PhotoText>
				</Card>;
			})}
		</Container>
		<Pagination
			label={translateToString('forms')}
			amount={instances[0]?.count ?? 0}
			currentCount={instances.length}
			steps={[DEFAULT_STEP, 75, 100]}
			onChange={(res) => {
				if (JSON.stringify(res) === JSON.stringify(pagination)) return;
				setPagination(res);
				refresh(res);
			}}
		/>

		<ComponentLoader loadingState={loadingState} allScreen />
	</>;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

function PhotosList(props: {
	instances: FormInstanceGalleryType[]
	defaultInstanceIndex: number
	closeModal: () => void,
	formId: number
}) {
	const [zoom, setZoom] = React.useState<number>(MIN_ZOOM);
	const [[currentInstanceIndex, fieldIndex, fieldValueIndex], setIndexes] = React.useState<[number, number, number]>([props.defaultInstanceIndex, 0, 0]);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);

	React.useEffect(() => {
		setLoadingState(LoadingStateEnum.LOADING);
	}, [currentInstanceIndex, fieldIndex, fieldValueIndex]);

	function keyHandler(event: KeyboardEvent) {
		event.stopPropagation();
		event.preventDefault();
		switch (event.key) {
			case 'ArrowUp':
				zoomHandler(1);
				return;
			case 'ArrowDown':
				zoomHandler(-1);
				return;
			case 'Escape':
				props.closeModal();
				return;
			case 'ArrowLeft':
				changeIndex(-1);
				return;
			case 'ArrowRight':
				changeIndex(1);
		}
	}

	function zoomHandler(direction?: number) {
		setZoom(currentZoom => {
			if (direction !== undefined) {
				const newZoom = currentZoom + direction * ZOOM_STEP;
				if (newZoom < MIN_ZOOM || newZoom > MAX_ZOOM) {
					return currentZoom;
				}
				return newZoom;
			}
			if (currentZoom === MAX_ZOOM) {
				return MIN_ZOOM;
			}
			return MAX_ZOOM;

		});
	}

	React.useEffect(() => {
		document.addEventListener('keydown', keyHandler, false);
		return () => {
			document.removeEventListener('keydown', keyHandler, false);
		};
	}, [fieldIndex]);

	const currentInstance = props.instances[currentInstanceIndex];
	if (!currentInstance) return null;
	const instanceImageLength = currentInstance.images.reduce((acc, curr) => acc + curr.value.length, 0);
	const currentIndex = currentInstance.images.reduce((acc, curr, index) => index < fieldIndex ? acc + curr.value.length : index === fieldIndex ? acc + fieldValueIndex + 1 : acc, 0);
	const currentField = currentInstance.images[fieldIndex];
	if (!currentField) return null;
	const currentImage = currentField.value[fieldValueIndex];
	if (!currentImage) return null;

	function changeIndex(modification: number) {
		setIndexes(([currentInstanceIndex, fieldIndex, fieldValueIndex]) => {
			const currentInstance = props.instances[currentInstanceIndex];
			const curerntField = currentInstance.images[fieldIndex];
			if (modification === -1 && currentInstanceIndex + fieldIndex + fieldValueIndex === 0) return [currentInstanceIndex, fieldIndex, fieldValueIndex];
			if (modification === 1 && currentInstanceIndex >= props.instances.length - 1 && fieldIndex >= currentInstance.images.length - 1 && fieldValueIndex + 1 >= curerntField.value.length - 1) return [currentInstanceIndex, fieldIndex, fieldValueIndex];
			let newFieldValueIndex = fieldValueIndex + modification;
			let newFieldIndex = fieldIndex;
			let newCurrentInstance = currentInstanceIndex;
			if (newFieldValueIndex > curerntField.value.length - 1) {
				newFieldValueIndex = 0;
				if (fieldIndex + 1 > currentInstance.images.length - 1) {
					newFieldIndex = 0;
					newCurrentInstance = currentInstanceIndex + 1;
				} else {
					newFieldIndex = fieldIndex + 1;
				}
			} else if (newFieldValueIndex < 0) {
				if (newFieldIndex - 1 < 0) {
					newCurrentInstance = currentInstanceIndex - 1;
					newFieldIndex = props.instances[newCurrentInstance].images.length - 1;
				} else {
					newFieldIndex = fieldIndex - 1;
				}
				newFieldValueIndex = props.instances[newCurrentInstance].images[newFieldIndex].value.length - 1;
			}
			return [newCurrentInstance, newFieldIndex, newFieldValueIndex];
		});
	}

	return <Wrapper>
		{currentInstance.linked_company_id && <Details>
			<FlexDiv gap='5px'>
				{currentInstance.linked_company_name}
				<img src={new_tab_white} style={{ height: 15, cursor: 'pointer' }} onClick={() => window.open(`/companies?id=${currentInstance.linked_company_id}`, '_blank')} />
			</FlexDiv>
			<div>{currentIndex} / {instanceImageLength}</div>
		</Details>}

		<ImageAndSwapperWrapper>
			<ImageSwapper onClick={() => changeIndex(-1)} />
			<ImageWrapper onClick={() => zoomHandler()} zoom={zoom}>
				<Image
					zoom={zoom}
					loadingState={loadingState}
					src={currentImage.url}
					onError={() => setLoadingState(LoadingStateEnum.ERROR)}
					onLoad={() => setLoadingState(LoadingStateEnum.LOADED)}
				/>
				{loadingState === LoadingStateEnum.LOADING && <div style={{ display: 'inline-block', height: '50px', width: '200px', marginTop: '60px' }}>
					<ComponentLoader loadingState={loadingState} noBackground />
				</div>}
			</ImageWrapper>
			<ImageSwapper right onClick={() => changeIndex(1)} />
		</ImageAndSwapperWrapper>
		<Details>
			<FlexDiv gap='5px'>{currentField.field_name} {fieldValueIndex + 1} / {currentField.value.length}
				<img src={new_tab_white} style={{ height: 15, cursor: 'pointer' }} onClick={() => window.open(`/form-instance-list?id=${props.formId}&instance=${currentInstance.uuid}#list`, '_blank')} />
			</FlexDiv>
			<UserBlock userId={currentInstance.created_by} />
			{moment(currentInstance.made_at).format('DD/MM/YYYY - HH:mm')}
		</Details>
	</Wrapper>;
}

const Image = styled.img<{ loadingState: LoadingStateEnum, zoom: number }>`
	${(props) => props.loadingState === LoadingStateEnum.LOADING ? `
		display: none;
	` : ''}
	transform: scale(${({ zoom }) => zoom});
`;

const Wrapper = styled.div`
	display: grid;
	grid-template-rows: auto 1fr auto;
	gap: 10px;
	padding: 10px;
	height: 100vh;
	width: 100vw;
	font-size: 12px;
	color: white;
`;

const ImageWrapper = styled.div<{ zoom: number }>`
	overflow: auto;
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	cursor: ${({ zoom }) => zoom == MAX_ZOOM ? 'zoom-out' : 'zoom-in'};
`;

const Details = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
`;

const ImageSwapper = styled.div<{ right?: boolean }>`
    width: 30px;
    height: 30px;
    transform: rotate(${p => p.right ? '-' : ''}90deg);
    background-image: url('${CheverontWhite}');
    background-repeat: no-repeat;
    background-position: center;
    background-size: 30px;
    cursor: pointer;
`;


const ImageAndSwapperWrapper = styled.div`
  overflow: hidden;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: center;
`;
